/**
 * One body, two boxes — `docs/property-maps-v2.md` §§6.1, 6.4, 7;
 * `docs/proposal.md` §8.1.
 *
 * A centred `Modal` on a desktop and an iPad, a bottom `PanelSheet` on a
 * phone. Writing both would be writing each of them twice, which is how two
 * surfaces meant to say the same thing stop saying it — so this is the
 * switch, and **the body is the same component either way**. It is the same
 * argument `ToolPanel` makes for the list engine's five panels
 * (`design-system.md` §6.1), applied to the property map's eight dialogs and
 * to the proposal's sign card.
 *
 * `usePhone()` decides by default, at `PHONE_WIDTH` — the product's one
 * answer to "is this a phone"; §7's "< 768" and this ≤ 720 would be two, and
 * the windows between them are ones nobody has. **`phone` overrides it**,
 * because the quote builder renders the proposal inside a 390px preview
 * frame in a 1400px window, where a media query is measuring the wrong box.
 *
 * It lives in `components/` rather than under one feature for the obvious
 * reason: the second feature that needed it would otherwise have written a
 * third sheet.
 */
import type { ReactNode } from 'react'
import { Modal } from './overlays.js'
import { PanelSheet } from './panel-sheet.js'
import { usePhone } from '../lib/use-narrow.js'

export function SheetOrDialog({
  title, sub, mark, width = 520, footer, footerNote, onClose, onBack, backLabel,
  tall, layer, phone: forced, children,
}: {
  title: string
  sub?: string
  /** The dialog's 30px tile. A sheet has no room for one and leaves it out. */
  mark?: ReactNode
  width?: number
  footer?: ReactNode
  /** The dialog's footer line. A sheet puts it in the body, where it fits. */
  footerNote?: string
  onClose: () => void
  onBack?: () => void
  backLabel?: string
  tall?: boolean
  layer?: number
  /** Forces the sheet regardless of the window (the builder's preview frame). */
  phone?: boolean
  children: ReactNode
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone

  if (phone) {
    return (
      <PanelSheet
        open
        onClose={onClose}
        title={title}
        {...(sub ? { sub } : {})}
        {...(onBack ? { onBack } : {})}
        {...(backLabel ? { backLabel } : {})}
        {...(footer ? { footer } : {})}
        {...(tall ? { tall } : {})}
        {...(layer === undefined ? {} : { layer })}
      >
        {children}
        {footerNote && (
          <p className="m-0 px-4 pb-2 pt-3 text-sub text-text-muted">{footerNote}</p>
        )}
      </PanelSheet>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      {...(sub ? { subtitle: sub } : {})}
      {...(mark ? { mark } : {})}
      width={width}
      {...(footer ? { footer } : {})}
      {...(footerNote ? { footerNote } : {})}
      {...(layer === undefined ? {} : { layer })}
    >
      {children}
    </Modal>
  )
}
